import { View } from '../types';

export interface FooterCardConfig {
  title: string;
  details: string;
  actionLabel: string;
  view: View.PRIVACY | View.TERMS | View.LEGAL | View.CONTACT;
}

export const footerInfoCards: FooterCardConfig[] = [
  {
    title: 'Privacy',
    details: 'We protect user data with encrypted storage, controlled access, and audited handling policies.',
    actionLabel: 'Read Privacy',
    view: View.PRIVACY,
  },
  {
    title: 'Terms',
    details: 'Service usage rules, account responsibilities, and billing conditions are clearly documented.',
    actionLabel: 'View Terms',
    view: View.TERMS,
  },
  {
    title: 'Legal',
    details: 'Compliance, licensing, and governance details for teams using Scrudix in production.',
    actionLabel: 'Legal Docs',
    view: View.LEGAL,
  },
  {
    title: 'Contact',
    details: 'Need help with setup, security review, or enterprise onboarding? Reach the team directly.',
    actionLabel: 'Open Contact',
    view: View.CONTACT,
  },
];

export interface LegalContentSection {
  heading: string;
  body: string;
}

export interface LegalContent {
  badge: string;
  title: string;
  subtitle: string;
  lastUpdated: string;
  sections: LegalContentSection[];
}

export const legalContentByPage: Record<
  View.PRIVACY | View.TERMS | View.LEGAL | View.CONTACT,
  LegalContent
> = {
  [View.PRIVACY]: {
    badge: 'Privacy',
    title: 'Privacy Policy',
    subtitle: 'How Scrudix collects, uses, and protects your information.',
    lastUpdated: 'Updated February 20, 2026',
    sections: [
      {
        heading: 'Data We Collect',
        body: 'We collect account details, usage telemetry, and user-submitted content required to provide product functionality and support.',
      },
      {
        heading: 'How Data Is Used',
        body: 'Data is used to authenticate users, deliver features, improve product quality, and maintain security monitoring.',
      },
      {
        heading: 'Retention And Deletion',
        body: 'Data is retained only as long as required for service delivery, legal obligations, and operational security. Deletion requests are honored based on account controls.',
      },
      {
        heading: 'Security Controls',
        body: 'Scrudix uses encryption in transit, access controls, audit logging, and environment-level security practices for production systems.',
      },
    ],
  },
  [View.TERMS]: {
    badge: 'Terms',
    title: 'Terms Of Service',
    subtitle: 'Rules and responsibilities when using Scrudix services.',
    lastUpdated: 'Updated February 20, 2026',
    sections: [
      {
        heading: 'Account Responsibilities',
        body: 'You are responsible for maintaining account security, protecting credentials, and ensuring authorized use within your organization.',
      },
      {
        heading: 'Acceptable Use',
        body: 'You must not use Scrudix for unlawful activities, abuse, unauthorized data access, or service disruption attempts.',
      },
      {
        heading: 'Billing And Subscription',
        body: 'Paid plans renew according to your billing cycle. Subscription changes are prorated and reflected in the next invoice.',
      },
      {
        heading: 'Termination',
        body: 'Accounts may be suspended for material violations of these terms. You may terminate your subscription at any time.',
      },
    ],
  },
  [View.LEGAL]: {
    badge: 'Legal',
    title: 'Legal And Compliance',
    subtitle: 'Governance, compliance posture, and legal notices.',
    lastUpdated: 'Updated February 20, 2026',
    sections: [
      {
        heading: 'Compliance Program',
        body: 'Scrudix follows a security and compliance program that includes formal controls, policy reviews, and monitoring processes.',
      },
      {
        heading: 'Intellectual Property',
        body: 'All product assets, branding, and platform components are protected by applicable intellectual property laws.',
      },
      {
        heading: 'Third-Party Services',
        body: 'Scrudix may rely on vetted infrastructure and provider integrations. Vendor practices are reviewed as part of risk management.',
      },
      {
        heading: 'Legal Requests',
        body: 'Questions regarding legal process, contracts, and compliance evidence can be sent to legal@scrudix.ai.',
      },
    ],
  },
  [View.CONTACT]: {
    badge: 'Contact',
    title: 'Contact And Support',
    subtitle: 'How to reach sales, support, and legal teams.',
    lastUpdated: 'Updated February 20, 2026',
    sections: [
      {
        heading: 'General Support',
        body: 'Product support and onboarding requests: support@scrudix.ai',
      },
      {
        heading: 'Sales And Partnerships',
        body: 'Enterprise pricing, partnerships, and procurement: sales@scrudix.ai',
      },
      {
        heading: 'Security And Legal',
        body: 'Compliance, legal, and security inquiries: legal@scrudix.ai',
      },
      {
        heading: 'Response Times',
        body: 'Support responses are typically provided within one business day. Enterprise support follows SLA commitments.',
      },
    ],
  },
};

export interface PricingPlanContent {
  name: string;
  monthlyPrice: number;
  description: string;
  cta: string;
  popular?: boolean;
  features: string[];
  limits?: string[];
}

export const pricingPlans: PricingPlanContent[] = [
  {
    name: 'Starter',
    monthlyPrice: 0,
    description: 'Best for solo experimentation.',
    cta: 'Get Started',
    features: [
      'Unlimited Lumi text chat',
      '2 voice chats per month',
      'File uploads & attachments',
      '30 Scrudix Studio chats per month',
      'Up to 2 team members',
      'Export & download outputs',
      'Community support',
    ],
    limits: ['Image & video generation locked', 'Limited 2 members on Team Dashboard'],
  },
  {
    name: 'Pro',
    monthlyPrice: 500,
    description: 'Best for growing product teams.',
    cta: 'Subscribe Pro',
    popular: true,
    features: [
      'Unlimited Lumi text + voice',
      'Unlimited Scrudix Studio chat',
      'Image & video generation',
      'Scrudix x Higgsfield access',
      'Access to GPT-5.5+, Claude 4.8 Opus and Fable 5 agents',
      'Advanced code analysis and refactoring',
      'AI-powered debugging agent',
      'Automated test generation',
      'Priority email support',
    ],
    limits: ['No Pro+ team dashboard'],
  },
  {
    name: 'Pro+',
    monthlyPrice: 1250,
    description: 'Best for teams that need shared workspaces and admin controls.',
    cta: 'Subscribe Pro+',
    features: [
      'Everything in Pro',
      'Team dashboard & collaboration',
      'Invite team members',
      'Role management & analytics',
      'Shared projects & assets',
      'Admin controls & billing',
      'Priority live chat support',
    ],
  },
];

export const pricingFaqs: Array<{ question: string; answer: string }> = [
  {
    question: 'How does billing work?',
    answer:
      'Billing is per-user with auto-renew enabled. Monthly plans renew every month. Annual plans are billed yearly with a 20% discount.',
  },
  {
    question: 'Which payment gateway do you use?',
    answer:
      'Gateway is auto-selected by region: Razorpay is preferred in India, Stripe in most other regions. Currency is automatically localized by country.',
  },
  {
    question: 'Can I switch plans at any time?',
    answer:
      'Yes. Upgrade instantly or downgrade at the end of your current billing cycle.',
  },
  {
    question: 'What counts as an AI operation?',
    answer:
      'Each AI task — code review, debug, legal scan, test generation, knowledge query — counts as one operation.',
  },
  {
    question: 'What is the difference between Pro and Pro+?',
    answer:
      'Pro unlocks unlimited voice chat, image/video generation, and Scrudix x Higgsfield for individuals. Pro+ adds the team dashboard, member invites, role management, shared projects, and admin analytics.',
  },
  {
    question: 'Do free quotas reset every month?',
    answer:
      'Yes. Starter voice chats and Scrudix Studio chats reset at the start of each calendar month.',
  },
];

export type DocsSectionId =
  | 'getting-started'
  | 'installation'
  | 'api-reference'
  | 'integrations'
  | 'security'
  | 'guides';

export const docsSections: Array<{ id: DocsSectionId; label: string }> = [
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'installation', label: 'Installation' },
  { id: 'api-reference', label: 'API Reference' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'security', label: 'Security' },
  { id: 'guides', label: 'Guides' },
];

export const docsSearchIndex: Array<{
  section: DocsSectionId;
  title: string;
  keywords: string[];
  summary: string;
}> = [
  {
    section: 'getting-started',
    title: 'Getting Started',
    keywords: ['getting started', 'quickstart', 'init', 'login', 'cli', 'setup'],
    summary: 'Install the CLI, authenticate, and initialize Scrudix in your project.',
  },
  {
    section: 'installation',
    title: 'Editor Installation',
    keywords: ['install', 'vs code', 'jetbrains', 'neovim', 'extension'],
    summary: 'Install Scrudix in VS Code, JetBrains, and Neovim with guided steps.',
  },
  {
    section: 'api-reference',
    title: 'API Reference',
    keywords: ['api', 'endpoint', 'curl', 'auth', 'bearer', 'rate limit'],
    summary: 'Reference for authentication, request payloads, and response contracts.',
  },
  {
    section: 'integrations',
    title: 'Integrations',
    keywords: ['github', 'slack', 'jira', 'gitlab', 'webhook', 'integration'],
    summary: 'Connect Scrudix to your repositories, messaging, and ticketing tools.',
  },
  {
    section: 'security',
    title: 'Security',
    keywords: ['security', 'encryption', 'compliance', 'soc 2', 'hipaa', 'gdpr'],
    summary: 'Understand encryption, access controls, audit trails, and compliance posture.',
  },
  {
    section: 'guides',
    title: 'Guides',
    keywords: ['guide', 'best practices', 'workflow', 'review', 'knowledge'],
    summary: 'Practical playbooks for code review, legal analysis, and team knowledge.',
  },
];

export const docsGuideCards: Array<{
  id: string;
  category: string;
  title: string;
  time: string;
  description: string;
}> = [
  {
    id: 'code-review',
    category: 'Engineering',
    title: 'Code Review Best Practices',
    time: '10 min read',
    description: 'Set review rules, tune severity thresholds, and integrate checks into CI pipelines.',
  },
  {
    id: 'contract-analysis',
    category: 'Legal',
    title: 'Contract Analysis Workflow',
    time: '8 min read',
    description: 'Build repeatable legal review flows with risk categorization and escalation rules.',
  },
  {
    id: 'knowledge-base',
    category: 'Knowledge',
    title: 'Knowledge Base Design',
    time: '12 min read',
    description: 'Capture institutional knowledge and make it discoverable for engineering and support teams.',
  },
];
